/**
 * neopac — Calculadora de costos. CONTROLADOR DE INTERFAZ.
 *
 * Este archivo NO calcula nada. Toda la aritmética vive en
 * neopac-calc-engine.js y todas las tarifas en neopac-calc-config.json.
 * Aquí solo se lee el formulario, se llama al motor y se pinta.
 *
 * Reglas que este archivo respeta:
 *  - Cero lógica de negocio: ningún porcentaje, umbral ni tarifa.
 *  - Las alertas se pintan desde los objetos del motor; nunca se
 *    re-deriva una condición de alerta aquí.
 *  - Estado en memoria. Sin localStorage ni sessionStorage.
 *  - Se redondea SOLO al mostrar: dinero 2 decimales half-up,
 *    porcentajes 1 decimal (§1.6). El motor no redondea.
 *  - La única petición de red es el JSON de tarifas por asset_url,
 *    que es lo que prescribe la §10.2.
 */

/* ══════════════════ Formato (solo despliegue) ══════════════════ */

/** Redondeo half-up explícito: toFixed usa banker's en algunos casos. */
function redondear(n, dec) {
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** dec;
  const x = n * f;
  const r = Math.sign(x) * Math.round(Math.abs(x) + Number.EPSILON * Math.abs(x));
  return r / f;
}

const fmtMXN = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2
});

/** Dinero a 2 decimales half-up. */
function dinero(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return fmtMXN.format(redondear(n, 2));
}

/** Porcentaje a 1 decimal (§1.6). Recibe fracción. */
function pct(n, dec = 1) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${redondear(n * 100, dec).toFixed(dec)} %`;
}

/** Miles con separador, para escribir moneda mientras se teclea. */
function agruparMiles(entero) {
  return entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function limpiarNumero(txt) {
  const s = String(txt).replace(/[^\d.]/g, '');
  const partes = s.split('.');
  return partes.length > 2 ? `${partes[0]}.${partes.slice(1).join('')}` : s;
}

function aNumero(txt) {
  const n = parseFloat(limpiarNumero(txt));
  return Number.isFinite(n) ? n : 0;
}

/** Formatea moneda en el input conservando la posición del cursor. */
function formatearMientrasEscribe(input) {
  const antes = input.value;
  const caret = input.selectionStart ?? antes.length;
  const digitosAntes = antes.slice(0, caret).replace(/[^\d]/g, '').length;

  const limpio = limpiarNumero(antes);
  if (limpio === '') { input.value = ''; return; }
  const [ent, dec] = limpio.split('.');
  const salida = agruparMiles(ent || '0') + (dec !== undefined ? `.${dec.slice(0, 2)}` : '');
  input.value = salida;

  let vistos = 0, nuevoCaret = salida.length;
  for (let i = 0; i < salida.length; i += 1) {
    if (/\d/.test(salida[i])) vistos += 1;
    if (vistos >= digitosAntes) { nuevoCaret = i + 1; break; }
  }
  if (digitosAntes === 0) nuevoCaret = 0;
  try { input.setSelectionRange(nuevoCaret, nuevoCaret); } catch { /* input sin selección */ }
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** Chip VERIFICAR con enlace a la fuente oficial. */
function chipVerificar(url, titulo) {
  if (!url) return '';
  return ` <a class="nc-verif" href="${esc(url)}" target="_blank" rel="noopener"
    title="${esc(titulo || 'Dato marcado para verificar contra la fuente oficial')}">verificar ↗</a>`;
}

/* ══════════════════ Diccionarios de despliegue ══════════════════ */

const PLATAFORMA_NOMBRE = { ml: 'Mercado Libre', amazon: 'Amazon', tiktok: 'TikTok Shop' };

const ORIGEN_TASA = {
  configuracion: 'tasa de ley',
  capturado: 'la que tú capturaste',
  retencion_de_plataforma: 'la que ya te retuvo la plataforma'
};

const REGIMEN_TIP = {
  pf_plataformas_definitivo: 'Lo que te retienen ya es todo tu ISR. No declaras anual por estas ventas.',
  pf_plataformas_provisional: 'Lo retenido es un anticipo. Declaras al año y ahí se ajusta.',
  persona_moral: 'Tu empresa paga 30 % de la utilidad; lo retenido se te acredita.'
};

const REGIMEN_ETIQUETA_CORTA = {
  pf_plataformas_definitivo: 'Persona física — pago definitivo',
  pf_plataformas_provisional: 'Persona física — pago provisional',
  persona_moral: 'Persona moral (empresa)'
};

/** Estados vacíos. Cada bandera nombra qué falta y cómo darlo. */
const BANDERAS = {
  regimen_fiscal_no_capturado: {
    titulo: 'Falta tu régimen fiscal',
    cuerpo: 'Sin régimen no estimamos tu ISR: esa línea va en cero y tu utilidad real es menor de lo que ves.',
    accion: { texto: 'Elegir régimen', foco: '[data-nc-campo="regimen_fiscal"]' }
  },
  regimen_fiscal_desconocido: {
    titulo: 'Ese régimen no está en la lista',
    cuerpo: 'Elige uno de los tres.',
    accion: { texto: 'Elegir régimen', foco: '[data-nc-campo="regimen_fiscal"]' }
  },
  isr_efectivo_no_capturado: {
    titulo: 'Necesitamos tu tasa efectiva de ISR',
    cuerpo: 'En pago provisional el ISR es progresivo sobre tu utilidad del año; no hay una tasa por pedido que sea correcta, así que no la inventamos. Sale de tu declaración anual pasada, o pregúntale a tu contador.',
    accion: { texto: 'Capturar mi tasa', foco: '[data-nc-campo="isr_efectivo_pct_num"]' }
  },
  umbral_pago_definitivo_excedido: {
    titulo: 'Ya no calificas para pago definitivo',
    cuerpo: 'Tus ingresos del año pasan el tope (plataformas + sueldos + intereses). Cambia a pago provisional y revísalo con tu contador.',
    accion: { texto: 'Cambiar de régimen', foco: '[data-nc-campo="regimen_fiscal"]' }
  }
};

/* ══════════════════ Analítica (§10.4) ══════════════════ */

function evento(nombre, datos) {
  try {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `neopac_calc_${nombre}`, ...datos });
    }
  } catch { /* nunca truena si no hay dataLayer */ }
}

/**
 * Cubetas de precio para analítica. Los cortes salen del config para
 * que no se desincronicen de los umbrales reales; aquí no se declara
 * ningún número de negocio.
 */
function rangoPrecio(p, config) {
  if (!Number.isFinite(p) || p <= 0) return 'sin_precio';
  const cortes = (config?.amazon?.fba?.cortes_precio || []).slice().sort((a, b) => a - b);
  for (const corte of cortes) if (p < corte) return `menor_${corte}`;
  return cortes.length ? `${cortes[cortes.length - 1]}_o_mas` : 'sin_cortes';
}

/* ══════════════════ Controlador ══════════════════ */

class Calculadora {
  constructor(raiz, motor, config) {
    this.raiz = raiz;
    this.motor = motor;
    this.config = config;
    this.modo = 'queda';
    this.vista = 'a';
    this.mostrarMarkup = false;
    this.datos = {};
    this.ultimasBanderas = [];
  }

  $(sel) { return this.raiz.querySelector(sel); }
  $$(sel) { return Array.from(this.raiz.querySelectorAll(sel)); }

  montar() {
    this.pintarEncabezados();
    this.llenarCatalogos();
    this.enlazar();
    this.$('[data-nc-cargando]').hidden = true;
    this.$('[data-nc-salida]').hidden = false;
    this.actualizar();
  }

  /* ── Vigencia, fuentes, aviso fiscal, tarjeta RESICO ── */
  pintarEncabezados() {
    const c = this.config;
    const etiqueta = this.raiz.dataset.vigenciaLabel || 'Tarifas actualizadas al';
    let fecha = c.vigencia;
    try {
      const [a, m, d] = c.vigencia.split('-').map(Number);
      fecha = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
        .format(new Date(a, m - 1, d));
    } catch { /* si falla, se muestra la fecha ISO */ }
    const texto = `${etiqueta} ${fecha}`;
    this.$('[data-nc-vigencia]').textContent = texto;
    this.$('[data-nc-vigencia-pie]').textContent = `${texto}. Todos los montos son estimados.`;
    this.$('[data-nc-aviso-fiscal]').textContent = c.textos_legales.aviso_fiscal;

    const grupos = { mercado_libre: 'Mercado Libre', amazon: 'Amazon', tiktok_shop: 'TikTok Shop', fiscal: 'SAT y DOF', shopify: 'Shopify' };
    this.$('[data-nc-fuentes]').innerHTML = Object.entries(grupos).map(([clave, titulo]) => {
      const bloque = c.fuentes[clave] || {};
      const enlaces = Object.entries(bloque)
        .filter(([, url]) => typeof url === 'string' && url.startsWith('http'))
        .map(([k, url]) => `<a href="${esc(url)}" target="_blank" rel="noopener">${esc(k.replace(/_/g, ' '))}</a>`)
        .join('');
      return enlaces ? `<h4>${esc(titulo)}</h4>${enlaces}` : '';
    }).join('');

    const r = this.motor.informacionResico(c);
    this.$('[data-nc-resico]').innerHTML = `
      <p>Hay dos “1 %” distintos y hoy ninguno es el tuyo:</p>
      <ul>
        <li>La <b>retención de plataformas</b> por vender productos subió en 2026 a <b>${pct(c.impuestos.retencion_isr_con_rfc_pct_sobre_base)}</b>: es la que ya ves descontada aquí. Antes era más baja, y mucha gente todavía la recuerda así.</li>
        <li>El <b>${pct(r.tabla_anual[0].pct)} de RESICO</b> es otra cosa y no aplica si vendes en plataformas: la regla 3.13.3 te manda al régimen de plataformas tecnológicas.${chipVerificar(r.fuente, 'Confirmar con tu contador contra el texto vigente')}</li>
      </ul>
      <p>Si te dijeron “1 %”, revísalo con tu contador.</p>`;

    const ayuda = this.raiz.dataset.embalajeAyuda;
    if (ayuda) {
      const el = this.$('[data-nc-embalaje-ayuda]');
      el.textContent = ayuda;
      el.hidden = false;
    }
  }

  /* ── Catálogos que vienen del config ── */
  llenarCatalogos() {
    const selCat = this.$('[data-nc-categorias]');
    selCat.innerHTML = Object.entries(this.config.amazon.categorias)
      .map(([clave, cat]) => {
        const nombre = clave.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
        const marca = cat.autorizacion_previa ? ' (requiere autorización)' : '';
        return `<option value="${esc(clave)}"${clave === 'todo_lo_demas' ? ' selected' : ''}>${esc(nombre)}${marca}</option>`;
      }).join('');

    const selReg = this.$('[data-nc-regimenes]');
    selReg.innerHTML = '<option value="">— Elige tu régimen —</option>' +
      this.config.impuestos.regimen_fiscal_opciones.map((clave) => {
        const def = this.config.impuestos.regimenes_fiscales[clave];
        return `<option value="${esc(clave)}">${esc(REGIMEN_ETIQUETA_CORTA[clave] || def.etiqueta)}</option>`;
      }).join('');

    this.$$('[data-nc-tip]').forEach((el) => {
      el.setAttribute('title', el.dataset.ncTip);
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', `Ayuda: ${el.dataset.ncTip}`);
      el.setAttribute('tabindex', '0');
    });
  }

  /* ── Eventos ── */
  enlazar() {
    const form = this.$('[data-nc-form]');

    form.addEventListener('input', (e) => {
      if (e.target.matches('[data-nc-money]')) formatearMientrasEscribe(e.target);
      this.actualizar();
    });
    form.addEventListener('change', () => this.actualizar());
    form.addEventListener('submit', (e) => e.preventDefault());

    const tabs = this.$$('.nc-tab');
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.cambiarModo(tab.dataset.ncModo));
      tab.addEventListener('keydown', (e) => {
        const mapa = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
        if (!(e.key in mapa)) return;
        e.preventDefault();
        const destino = tabs[(mapa[e.key] + tabs.length) % tabs.length];
        destino.focus();
        this.cambiarModo(destino.dataset.ncModo);
      });
    });

    this.$$('[name^="nc-vista-"]').forEach((r) => {
      r.addEventListener('change', () => {
        this.vista = r.value;
        evento('vista', { vista: this.vista });
        this.actualizar();
      });
    });

    this.$('.nc-avanzado').addEventListener('toggle', (e) => {
      if (e.target.open) evento('modo_avanzado_abierto', {});
    });
  }

  cambiarModo(modo) {
    if (!modo || modo === this.modo) return;
    this.modo = modo;
    this.$$('.nc-tab').forEach((t) => {
      const activo = t.dataset.ncModo === modo;
      t.setAttribute('aria-selected', String(activo));
      t.tabIndex = activo ? 0 : -1;
    });
    this.$('#nc-panel-form').setAttribute('aria-labelledby', `nc-tab-${modo}`);
    evento('modo', { modo });
    this.actualizar();
  }

  /* ── Lectura del formulario ── */
  leer() {
    const d = {};
    this.$$('[data-nc-campo]').forEach((el) => {
      const k = el.dataset.ncCampo;
      if (el.type === 'checkbox') d[k] = el.checked;
      else if (el.tagName === 'SELECT') d[k] = el.value;
      else d[k] = el.value === '' ? null : aNumero(el.value);
    });
    d.plataforma = (this.$$('[name^="nc-plataforma-"]').find((r) => r.checked) || {}).value || 'ml';
    const ivaSel = this.$$('[name^="nc-iva-"]').find((r) => r.checked);
    d.tasa_iva = ivaSel ? parseFloat(ivaSel.value) : this.config.impuestos.iva_default;
    this.datos = d;
    return d;
  }

  /** Traduce el formulario a las entradas canónicas del motor (§2). */
  entradas(plataforma) {
    const d = this.datos;
    const e = {
      plataforma: plataforma || d.plataforma,
      precio_publico: d.precio_publico,
      costo_mercancia: d.costo_mercancia,
      costo_embalaje: d.costo_embalaje,
      costo_mercancia_incluye_iva: d.costo_mercancia_incluye_iva,
      tasa_iva: d.tasa_iva,
      tiene_rfc: d.tiene_rfc,
      unidades_por_pedido: d.unidades_por_pedido,
      costo_publicidad_pedido: d.costo_publicidad_pedido,
      descuento_vendedor: d.descuento_vendedor,
      largo_cm: d.largo_cm, ancho_cm: d.ancho_cm, alto_cm: d.alto_cm,
      peso_real_kg: d.peso_real_kg,
      regimen_fiscal: d.regimen_fiscal || null,
      ingresos_anuales_estimados: d.ingresos_anuales_estimados,
      isr_efectivo_pct: d.isr_efectivo_pct_num === null || d.isr_efectivo_pct_num === undefined
        ? null : d.isr_efectivo_pct_num / 100,
      // Mercado Libre
      ml_tipo_publicacion: d.ml_tipo_publicacion,
      ml_comision_pct: d.ml_comision_pct_num === null ? null : d.ml_comision_pct_num / 100,
      ml_reputacion: d.ml_reputacion,
      ml_modalidad_envio: d.ml_modalidad_envio,
      ml_costo_envio_base: d.ml_costo_envio_base,
      ml_ofrece_envio_gratis: d.ml_ofrece_envio_gratis,
      ml_tarifa_almacenamiento_dm3_dia: d.ml_tarifa_almacenamiento_dm3_dia,
      // Amazon
      amz_categoria: d.amz_categoria,
      amz_logistica: d.amz_logistica,
      amz_tamano: d.amz_tamano || undefined,
      amz_unidades_mes: d.amz_unidades_mes,
      amz_meses_en_plataforma: d.amz_meses_en_plataforma,
      amz_ventas_mes_anterior: d.amz_ventas_mes_anterior,
      amz_volumen_dm3: d.amz_volumen_dm3,
      amz_dias_en_bodega: d.amz_dias_en_bodega,
      tarifa_logistica_manual: d.tarifa_logistica_manual,
      mes: new Date().getMonth() + 1,
      // TikTok
      tt_inscrito_sfp: d.tt_inscrito_sfp,
      tt_incentivo_nuevo: d.tt_incentivo_nuevo,
      tt_paga_envio_vendedor: d.tt_paga_envio_vendedor,
      comision_creador_pct: (d.comision_creador_pct_num || 0) / 100,
      // Devoluciones
      tasa_devolucion: (d.tasa_devolucion_pct || 0) / 100,
      envio_regreso: d.costo_envio_real_tt
    };
    e.costo_envio_real = e.plataforma === 'tiktok' ? d.costo_envio_real_tt : d.costo_envio_real;
    return e;
  }

  /* ── Ciclo de render ── */
  actualizar() {
    this.leer();
    this.aplicarVisibilidad();
    const salida = this.$('[data-nc-salida]');
    try {
      if (this.modo === 'precio') this.renderPrecio(salida);
      else if (this.modo === 'comparar') this.renderComparar(salida);
      else this.renderQueda(salida);
    } catch (err) {
      salida.innerHTML = `<div class="nc-card"><p class="nc-help">No pudimos calcular con esos datos. Revisa los campos numéricos.</p></div>`;
      if (window.console) console.error('[neopac calc]', err);
    }
  }

  aplicarVisibilidad() {
    this.$$('[data-nc-solo]').forEach((el) => {
      el.hidden = !el.dataset.ncSolo.split(' ').includes(this.modo);
    });
    this.$$('[data-nc-plat]').forEach((el) => {
      el.hidden = this.modo !== 'comparar' && el.dataset.ncPlat !== this.datos.plataforma;
    });
    const full = this.$('[data-nc-mlfull]');
    if (full) full.hidden = this.datos.ml_modalidad_envio !== 'full';

    const reg = this.datos.regimen_fiscal || '';
    this.$$('[data-nc-cond]').forEach((el) => { el.hidden = el.dataset.ncCond !== reg; });
    this.$('[data-nc-regimen-tip]').textContent = REGIMEN_TIP[reg] || '';

    const plt = this.$('[data-nc-aviso-plt]');
    if (plt) plt.textContent = this.config.textos_legales.leyenda_aviso_pago_definitivo;
  }

  /* ── Modo 1 ── */
  renderQueda(destino) {
    const r = this.motor.calcular(this.entradas(), this.config);
    if (r.error) { destino.innerHTML = '<div class="nc-card"><p>Plataforma no reconocida.</p></div>'; return; }
    const b = this.vista === 'b';
    const v = b ? r.vista_b : r.vista_a;
    const utilidad = b ? v.utilidad_fiscal : v.utilidad_caja;

    destino.innerHTML = `
      <div class="nc-card">
        <p class="nc-grande-lbl">${b ? 'Tu utilidad real' : 'Te quedan'}</p>
        <p class="nc-grande ${utilidad < 0 ? 'neg' : ''}">${dinero(utilidad)}</p>
        <span class="nc-estimado">estimado · ${esc(PLATAFORMA_NOMBRE[r.plataforma])}</span>
        ${this.htmlMargen(v, r)}
        ${b ? '' : `<p class="nc-deposito">Te depositan: <b>${dinero(v.deposito_estimado)}</b> estimado</p>`}
      </div>
      ${this.htmlCascada(r)}
      ${b ? this.htmlVistaB(r) : ''}`;

    destino.querySelectorAll('[data-nc-swap]').forEach((btn) => {
      btn.addEventListener('click', () => { this.mostrarMarkup = !this.mostrarMarkup; this.actualizar(); });
    });

    this.renderAlertas(r.alertas);
    this.renderBanderas(this.recolectarBanderas(r));
    evento('calculo', {
      plataforma: r.plataforma, modo: this.modo, vista: this.vista,
      rango_precio: rangoPrecio(this.datos.precio_publico, this.config),
      regimen_fiscal: this.datos.regimen_fiscal || 'sin_capturar',
      alerta_perdida: r.alertas.some((a) => a.id === 'venta_con_perdida'),
      banderas: this.ultimasBanderas.join(',')
    });
  }

  /** §1.4: siempre etiquetado, margen sobre venta por defecto. */
  htmlMargen(v, r) {
    const semaforo = v.semaforo || 'rojo';
    const markup = r.cascada.markup_sobre_costo;
    const valor = this.mostrarMarkup ? markup : v.margen_sobre_venta;
    const etiqueta = this.mostrarMarkup ? 'markup sobre costo' : 'margen sobre venta';
    return `
      <div class="nc-medio">
        <span class="nc-pill ${semaforo}"><span class="nc-dot"></span>${pct(valor)}</span>
        <span class="nc-medio-lbl">${esc(etiqueta)}</span>
        <button type="button" class="nc-swap" data-nc-swap>
          ver ${this.mostrarMarkup ? 'margen sobre venta' : 'markup sobre costo'}
        </button>
      </div>`;
  }

  /** Cascada §3.1, con el envío en la posición que reporta el motor. */
  htmlCascada(r) {
    const filas = r.cascada.renglones.map((ren) => {
      const total = ren.signo === 0;
      const monto = ren.signo === -1 ? -ren.monto : ren.monto;
      const posicion = ren.id === 'costo_envio_vendedor'
        ? `<span class="nc-pos">${ren.posicion === 'dentro_deposito'
            ? 'Dentro del depósito: la plataforma lo descuenta de tu liquidación.'
            : 'Fuera del depósito: lo pagas directo a la paquetería.'}</span>` : '';
      return `<div class="nc-row ${total ? 'tot' : ''}">
        <span class="n">${total ? '' : ren.paso}</span>
        <span>${esc(ren.etiqueta)}${posicion}</span>
        <span class="v ${monto < 0 ? 'neg' : ''}">${dinero(monto)}</span>
      </div>`;
    }).join('');
    return `<details class="nc-casc"><summary>Ver el desglose completo</summary>
      <div class="nc-rows">${filas}</div>
      <p class="nc-meta">Todos los montos son estimados. El orden es el de la cascada oficial.</p>
    </details>`;
  }

  /* ── Vista B ── */
  htmlVistaB(r) {
    const v = r.vista_b;
    const det = v.isr_efectivo_detalle;
    const conc = v.conciliacion_isr;

    const ivaLinea = v.iva_acreditable_permitido
      ? `<div class="kv"><dt>IVA acreditable</dt><dd>${dinero(v.iva_acreditable)}</dd></div>`
      : `<div class="kv"><dt>IVA acreditable</dt><dd class="nc-cero">${dinero(0)}</dd></div>
         <p class="nc-leyenda">${esc(v.iva_acreditable_leyenda)}
         ${v.iva_no_acreditable > 0 ? `<br><b>Se te queda como costo: ${dinero(v.iva_no_acreditable)} estimado.</b>` : ''}</p>`;

    const isrBloque = det.regimen_fiscal ? `
      <div class="nc-bl">
        <h4>ISR de esta venta <span style="float:right">${dinero(-v.isr_efectivo)}</span></h4>
        <p class="nc-meta">
          <b>Régimen:</b> ${esc(det.regimen_etiqueta)}<br>
          <b>Se calcula sobre:</b> ${esc(v.isr_efectivo_base_aplicada || '—')}<br>
          <b>Tasa:</b> ${v.isr_efectivo_pct !== null ? pct(v.isr_efectivo_pct) : '—'}, ${esc(ORIGEN_TASA[v.isr_efectivo_pct_origen] || 'sin capturar')}
        </p>
        ${det.leyenda_pago_definitivo ? `<p class="nc-leyenda">${esc(det.leyenda_pago_definitivo)}</p>` : ''}
        <p class="nc-leyenda">${esc(v.isr_efectivo_leyenda)}</p>
      </div>` : '';

    let concBloque = '';
    if (det.regimen_fiscal) {
      let pie;
      if (!conc.computable) {
        pie = `<p class="nc-conc-s pend">Falta tu tasa para calcular esta mitad.</p>`;
      } else if (conc.tipo_saldo === 'sin_saldo') {
        pie = `<p class="nc-conc-s ok">Estás a mano. Saldo: ${dinero(0)}</p>
               <p class="nc-meta">Con pago definitivo siempre empatan: lo retenido <b>es</b> el impuesto. Nada que declarar por estas ventas.</p>`;
      } else if (conc.tipo_saldo === 'a_pagar') {
        pie = `<p class="nc-conc-s pagar">Saldo a pagar: ${dinero(conc.saldo)} estimado</p>
               <p class="nc-meta">Te faltará cubrir esa diferencia en tu declaración. Lo retenido se te acredita.</p>`;
      } else {
        pie = `<p class="nc-conc-s ok">Saldo a favor: ${dinero(Math.abs(conc.saldo))} estimado</p>
               <p class="nc-meta">Te retuvieron de más; se te acredita en tu declaración.</p>`;
      }
      concBloque = `
        <div class="nc-conc">
          <h4>Lo que ya te retuvieron vs. lo que te toca</h4>
          <p class="nc-meta">${esc(conc.leyenda)}</p>
          <div class="nc-conc-2">
            <div class="nc-conc-c"><span>Ya te retuvieron</span><b>${dinero(conc.isr_retenido)}</b></div>
            <div class="nc-conc-c"><span>Te toca de ISR</span><b>${conc.computable ? dinero(conc.isr_causado) : '—'}</b></div>
          </div>
          ${pie}
        </div>`;
    }

    return `
      <div class="nc-bl">
        <h4>Tu utilidad fiscal, paso a paso</h4>
        <dl>
          <div class="kv"><dt>Base gravable (sin IVA)</dt><dd>${dinero(v.base_gravable)}</dd></div>
          <div class="kv"><dt>Gastos${v.iva_acreditable_permitido ? ' sin IVA' : ' (con IVA, no acreditable)'}</dt><dd>−${dinero(v.gastos_sin_iva)}</dd></div>
          ${ivaLinea}
          <div class="kv"><dt>Utilidad antes de ISR</dt><dd>${dinero(v.utilidad_antes_de_isr)}</dd></div>
          <div class="kv"><dt>ISR estimado</dt><dd>−${dinero(v.isr_efectivo)}</dd></div>
          <div class="kv"><dt><b>Utilidad fiscal</b></dt><dd><b>${dinero(v.utilidad_fiscal)}</b></dd></div>
        </dl>
        <p class="nc-meta">Retenciones acreditables: <b>${dinero(v.retenciones_acreditables)}</b>. ${esc(v.leyenda)}</p>
      </div>
      ${isrBloque}
      ${concBloque}`;
  }

  /* ── Modo 2 ── */
  renderPrecio(destino) {
    const e = this.entradas();
    e.margen_objetivo = (this.datos.margen_objetivo_pct || 0) / 100;
    e.costo_envio_vendedor = e.plataforma === 'ml' ? 0 : (e.costo_envio_real || 0);
    e.precio_referencia_para_pct = this.datos.precio_publico;
    const r = this.motor.resolverPrecioPorPlataforma(e, this.config);

    if (!r.viable) {
      destino.innerHTML = `<div class="nc-card">
        <p class="nc-grande-lbl">A cómo lo vendo</p>
        <p class="nc-grande neg" style="font-size:22px;line-height:1.3">Con esos parámetros no hay precio que funcione</p>
        <p class="nc-help" style="text-align:center">Baja tu margen objetivo, baja costos, o revisa el % de comisión.</p>
      </div>`;
      this.renderAlertas([]);
      this.renderBanderas([]);
      return;
    }

    destino.innerHTML = `<div class="nc-precios">
      <div class="nc-precio-c">
        <span>Precio mínimo</span>
        <b>${dinero(r.precio_minimo)}</b>
        <p>Estimado. Cubre tus costos y las retenciones. <b>No incluye el ISR</b> que pagarás en tu declaración.</p>
      </div>
      <div class="nc-precio-c sug">
        <span>Precio sugerido</span>
        <b>${dinero(r.precio_sugerido)}</b>
        <p>Estimado. Tu precio mínimo más el margen que pediste (${pct((this.datos.margen_objetivo_pct || 0) / 100)} sobre venta).</p>
      </div>
    </div>
    ${r.rama_precio_sugerido === 'sfp_topado'
      ? `<div class="nc-bl"><p class="nc-meta">A ese precio la tarifa SFP ya llegó a su tope, así que la resolvimos como monto fijo.</p></div>` : ''}`;

    this.renderAlertas([]);
    this.renderBanderas([]);
    evento('calculo', { modo: this.modo, plataforma: e.plataforma, viable: r.viable });
  }

  /* ── Modo 3 ── */
  renderComparar(destino) {
    const cmp = this.motor.compararPlataformas(this.entradas(), this.config);
    const b = this.vista === 'b';

    // En Vista B un resultado incompleto no compite contra uno completo.
    const incompleto = (r) => (r.vista_b.isr_efectivo_banderas || []).length > 0;
    const hayIncompletos = b && cmp.resultados.some(incompleto);

    const tarjetas = cmp.resultados.map((r) => {
      const gana = !b && r.plataforma === cmp.mejor_plataforma;
      const util = b ? r.vista_b.utilidad_fiscal : r.utilidad_caja;
      const cuerpo = b && incompleto(r)
        ? `<div class="nc-incompleto"><b>Incompleto</b>${(r.vista_b.isr_efectivo_banderas || [])
              .map((f) => esc((BANDERAS[f] || {}).titulo || f)).join('. ')}. No lo comparamos contra los demás hasta tener el dato.</div>`
        : `<div class="nc-comp-n ${util < 0 ? 'neg' : ''}">${dinero(util)}</div>
           <p class="nc-help">estimado · ${esc(b ? 'utilidad fiscal' : 'utilidad de caja')}</p>
           <p class="nc-help">Margen sobre venta: ${pct(b ? r.vista_b.margen_sobre_venta : r.margen_sobre_venta)}</p>
           ${b ? '' : `<p class="nc-help">Te depositan: ${dinero(r.deposito_estimado)}</p>`}`;
      return `<div class="nc-comp-c ${gana ? 'gana' : ''}">
        <div class="nc-comp-h"><h4>${esc(PLATAFORMA_NOMBRE[r.plataforma])}</h4>
          ${gana ? '<span class="nc-gana-tag">deja más</span>' : ''}</div>
        ${cuerpo}
      </div>`;
    }).join('');

    destino.innerHTML = `<div class="nc-comp">${tarjetas}</div>
      ${hayIncompletos ? `<div class="nc-bl"><p class="nc-meta">No marcamos ganador en “Tu utilidad real” porque a alguna plataforma le falta un dato. Comparar un número conocido contra uno desconocido no sirve de nada.</p></div>` : ''}
      <div class="nc-bl"><p class="nc-meta">Los tres cálculos usan el mismo producto y el mismo precio, con los ajustes por plataforma que capturaste arriba.</p></div>`;

    const todas = cmp.resultados.flatMap((r) =>
      r.alertas.map((a) => ({ ...a, _plataforma: PLATAFORMA_NOMBRE[r.plataforma] })));
    this.renderAlertas(todas);
    this.renderBanderas([...new Set(cmp.resultados.flatMap((r) => this.recolectarBanderas(r)))]);
    evento('calculo', { modo: this.modo, rango_precio: rangoPrecio(this.datos.precio_publico, this.config), mejor: cmp.mejor_plataforma });
  }

  /* ── Alertas: se pintan tal cual las entrega el motor ── */
  renderAlertas(alertas) {
    const cont = this.$('[data-nc-alertas]');
    if (!alertas || !alertas.length) { cont.innerHTML = ''; return; }
    cont.innerHTML = alertas.map((a) => {
      const { titulo, cuerpo } = this.copyAlerta(a);
      const cifras = Object.entries(a.montos || {})
        .filter(([, v]) => Number.isFinite(v))
        .map(([k, v]) => `${esc(k.replace(/_/g, ' '))}: ${k.includes('proporcion') || k.includes('margen') ? pct(v) : dinero(v)}`)
        .join(' · ');
      return `<div class="nc-a ${esc(a.severidad)}">
        <h5>${esc(a._plataforma ? `${a._plataforma}: ` : '')}${esc(titulo)}</h5>
        <p>${cuerpo}</p>
        ${cifras ? `<p class="cifras">${cifras}</p>` : ''}
      </div>`;
    }).join('');
  }

  /** Copy por id de alerta. Las cifras vienen del motor, no se recalculan. */
  copyAlerta(a) {
    const m = a.montos || {};
    const d = a.datos || {};
    switch (a.id) {
      case 'venta_con_perdida':
        return { titulo: 'Estás vendiendo con pérdida', cuerpo: `Cada venta te cuesta ${dinero(Math.abs(m.utilidad_caja))} estimado. Sube el precio o baja costos.` };
      case 'cerca_de_umbral_comision':
        return d.lado === 'arriba'
          ? { titulo: 'Estás a un peso de un salto de comisión', cuerpo: `Tu precio está ${dinero(m.distancia)} arriba del umbral de ${dinero(m.umbral)}. Bajarlo tantito puede dejarte más.` }
          : { titulo: 'Vas llegando a un umbral de comisión', cuerpo: `Te faltan ${dinero(m.distancia)} para el umbral de ${dinero(m.umbral)}. Si lo cruzas, la comisión sube.` };
      case 'sin_rfc_costo':
        return { titulo: 'Sin RFC pierdes dinero en cada venta', cuerpo: `Te retienen ${dinero(m.diferencia)} estimado de más, y sin RFC no se genera constancia a tu nombre: no lo recuperas.` };
      case 'perdida_si_devolucion':
        return { titulo: 'Si te lo devuelven, pierdes', cuerpo: `Una devolución de este pedido te cuesta ${dinero(m.perdida_total)} estimado${Number.isFinite(m.ventas_buenas_equivalentes) ? `, o sea ${redondear(m.ventas_buenas_equivalentes, 1)} ventas buenas` : ''}. La tarifa de envío no se reembolsa.` };
      case 'comision_mayor_que_utilidad':
        return { titulo: 'La comisión es mayor que tu utilidad', cuerpo: `La plataforma se lleva ${dinero(m.comision_plataforma)} y a ti te quedan ${dinero(m.utilidad_caja)}.` };
      case 'envio_se_come_margen':
        return { titulo: 'Tu envío se come el margen', cuerpo: `El envío equivale al ${pct(m.proporcion)} de tu utilidad bruta. Comparado sobre base ${esc(d.base_comparacion === 'sin_iva' ? 'sin IVA' : 'con IVA')}, porque tu costo de mercancía ${d.costo_mercancia_incluye_iva ? 'sí' : 'no'} trae IVA.` };
      case 'incentivo_tiktok_termina':
        return { titulo: 'En 60 días tu comisión sube', cuerpo: `Hoy no pagas comisión. Al día 61 pagarías ${dinero(m.comision_al_dia_61)} y te quedarían ${dinero(m.utilidad_caja_al_dia_61)} estimado.` };
      case 'ml_precio_menor_al_minimo':
        return { titulo: 'Ese precio no se puede publicar', cuerpo: `Mercado Libre exige mínimo ${dinero(m.precio_minimo_publicacion)}.` };
      case 'ml_costo_fijo_variable':
        return { titulo: `Abajo de ${dinero(m.umbral)} el cargo puede variar`, cuerpo: `Te cobramos ${dinero(m.costo_fijo)} de costo fijo, pero desde 2026 puede sustituirse por un cargo variable según peso. Confírmalo en el simulador oficial.${chipVerificar(this.config.mercado_libre.simulador_url, d.verificar)}` };
      case 'tiktok_tope_sfp_activo':
        return { titulo: 'Ya topaste la tarifa de envío', cuerpo: `Arriba de ${dinero(this.config.tiktok_shop.sfp.precio_donde_se_activa_el_tope)} la tarifa se congela en ${dinero(m.tope)}. TikTok Shop premia el ticket alto.` };
      case 'tiktok_umbral_subsidio_envio':
        return { titulo: 'Tu precio queda abajo del envío subsidiado', cuerpo: `Te faltan ${dinero(m.faltante)} para llegar a ${dinero(m.gasto_minimo)}. Abajo de ahí el comprador paga el envío y tu conversión baja.` };
      case 'umbral_ingresos_anuales':
        return { titulo: 'Vas llegando a un umbral fiscal', cuerpo: `Proyectas ${dinero(m.ingresos_anuales_proyectados)} al año y el umbral es ${dinero(m.umbral)}. Cruzarlo cambia tu tratamiento fiscal: consúltalo con un contador.` };
      case 'comision_requiere_captura':
        return { titulo: 'Falta el % de comisión', cuerpo: `Esa categoría no tiene porcentaje publicado. Captúralo a mano.${chipVerificar(d.simulador_url, 'Consultar el porcentaje oficial')}` };
      default:
        return { titulo: a.id.replace(/_/g, ' '), cuerpo: 'Revisa este punto antes de fijar tu precio.' };
    }
  }

  /* ── Estados vacíos: nunca un cero silencioso ── */
  recolectarBanderas(r) {
    const b = [...(r.vista_b?.isr_efectivo_banderas || [])];
    const det = r.detalle || {};
    if (det.comision?.origen === 'default_orientativo') b.push('ml_comision_orientativa');
    if (det.almacenamiento?.tarifa_no_capturada) b.push('ml_almacenamiento_no_capturado');
    if (det.fba?.requiere_captura) b.push('amz_easyship_no_capturado');
    if (det.suscripcion?.mostrar_como_costo_fijo_mensual) b.push('amz_suscripcion_sin_prorratear');
    this.ultimasBanderas = b;
    return b;
  }

  renderBanderas(banderas) {
    const cont = this.$('[data-nc-banderas]');
    if (!banderas || !banderas.length) { cont.innerHTML = ''; return; }

    const extras = {
      ml_comision_orientativa: {
        titulo: 'Estamos usando un porcentaje de referencia',
        cuerpo: `Mercado Libre no publica una tabla estable. El porcentaje real lo ves al publicar.${chipVerificar(this.config.mercado_libre.simulador_url, 'Simulador oficial de costos')}`
      },
      ml_almacenamiento_no_capturado: {
        titulo: 'Falta la tarifa de almacenamiento de Full',
        cuerpo: 'Mercado Libre te la muestra en tu panel de vendedor. Captúrala arriba para que la incluyamos.',
        accion: { texto: 'Capturar tarifa', foco: '[data-nc-campo="ml_tarifa_almacenamiento_dm3_dia"]' }
      },
      amz_easyship_no_capturado: {
        titulo: 'Falta la tarifa de Easy Ship',
        cuerpo: `Amazon la publica en Seller Central. Captúrala en modo avanzado.${chipVerificar(this.config.fuentes.amazon.tarifas_easy_ship, 'Tarifas de Easy Ship')}`,
        accion: { texto: 'Capturar tarifa', foco: '[data-nc-campo="tarifa_logistica_manual"]' }
      },
      amz_suscripcion_sin_prorratear: {
        titulo: 'No prorrateamos la suscripción de Amazon',
        cuerpo: 'Pusiste 0 unidades al mes. La cuota es mensual y fija: se diluye con volumen, no por venta.',
        accion: { texto: 'Poner mis unidades', foco: '[data-nc-campo="amz_unidades_mes"]' }
      }
    };

    cont.innerHTML = banderas.map((f) => {
      const d = BANDERAS[f] || extras[f];
      if (!d) return '';
      return `<div class="nc-a falta">
        <h5>${esc(d.titulo)}</h5>
        <p>${d.cuerpo}</p>
        ${d.accion ? `<button type="button" class="nc-btn-min" data-nc-foco="${esc(d.accion.foco)}">${esc(d.accion.texto)}</button>` : ''}
      </div>`;
    }).join('');

    cont.querySelectorAll('[data-nc-foco]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const el = this.$(btn.dataset.ncFoco);
        if (!el) return;
        const caja = el.closest('.nc-cond, .nc-avanzado, .nc-fs');
        if (caja && caja.tagName === 'DETAILS') caja.open = true;
        const det = el.closest('details');
        if (det) det.open = true;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus();
      });
    });
  }
}

/* ══════════════════ Arranque ══════════════════ */

async function iniciar(raiz) {
  try {
    const [motor, config] = await Promise.all([
      import(raiz.dataset.engineUrl),
      fetch(raiz.dataset.configUrl, { credentials: 'omit' }).then((res) => {
        if (!res.ok) throw new Error(`config ${res.status}`);
        return res.json();
      })
    ]);
    new Calculadora(raiz, motor, config).montar();
  } catch (err) {
    const cargando = raiz.querySelector('[data-nc-cargando]');
    if (cargando) cargando.textContent = 'No pudimos cargar la calculadora. Recarga la página.';
    if (window.console) console.error('[neopac calc] arranque', err);
  }
}

document.querySelectorAll('[data-neopac-calc]').forEach(iniciar);
